import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../../app/app-store';
import type ClientApiV2 from '../client-api-v2';
import {
  getProgramDocuments,
  type ProgramDocuments,
} from '../program-documents-api';
import SliceRequest from '../slice-request';

type Entry = {
  data?: ProgramDocuments;
  status: 'idle' | 'pending' | 'succeeded' | 'failed';
  requestId?: string;
};
type State = { entries: Record<string, Entry> };
const initialState: State = { entries: {} };
const emptyEntry: Entry = { status: 'idle' };
export const programDocumentsKey = (programId: string, locale: string) =>
  `${programId}:${locale}`;

export const fetchProgramDocuments = SliceRequest<
  ProgramDocuments,
  { api: ClientApiV2; programId: string; locale: string }
>('fetchProgramDocuments', ({ api, programId, locale }) =>
  getProgramDocuments(api, programId, locale),
);

const programDocumentsSlice = createSlice({
  name: 'programDocuments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchProgramDocuments.pending, (state, { meta }) => {
      const key = programDocumentsKey(meta.arg.programId, meta.arg.locale);
      state.entries[key] = {
        ...state.entries[key],
        status: 'pending',
        requestId: meta.requestId,
      };
    });
    builder.addCase(
      fetchProgramDocuments.fulfilled,
      (state, { meta, payload }) => {
        const key = programDocumentsKey(meta.arg.programId, meta.arg.locale);
        if (state.entries[key]?.requestId !== meta.requestId) return;
        state.entries[key] = { data: payload, status: 'succeeded' };
      },
    );
    builder.addCase(fetchProgramDocuments.rejected, (state, { meta }) => {
      const key = programDocumentsKey(meta.arg.programId, meta.arg.locale);
      if (state.entries[key]?.requestId !== meta.requestId) return;
      // Do not retain now-inaccessible content after authorization/status changes.
      state.entries[key] = { status: 'failed' };
    });
  },
});

export const selectProgramDocumentsEntry = (
  state: RootState,
  programId: string,
  locale: string,
) =>
  state.programDocuments.entries[programDocumentsKey(programId, locale)] ??
  emptyEntry;

export const programDocumentsReducer = programDocumentsSlice.reducer;
